/**
 * Chess Statistics Service
 * 
 * Tracks chess game statistics including:
 * - Wins/losses/draws by game mode
 * - Statistics by AI difficulty level
 * - Statistics by multiplayer mode
 * - Win percentages
 * - Total games and play time
 */

import { ChessStatistics, ChessGameResult, GameMode, AIDifficulty, PlayerColor } from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class ChessStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'chess_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  /**
   * Initialize empty statistics
   */
  private createEmptyStats(): ChessStatistics {
    return {
      singlePlayer: {
        beginner: this.createEmptyGameStats(),
        intermediate: this.createEmptyGameStats(),
        expert: this.createEmptyGameStats()
      },
      multiplayer: {
        realtime: this.createEmptyGameStats(),
        turnbased: this.createEmptyGameStats()
      },
      totalGames: 0,
      totalPlayTime: 0,
      longestGame: 0,
      shortestWin: Infinity
    };
  }

  /**
   * Create empty game stats
   */
  private createEmptyGameStats() {
    return {
      wins: 0,
      losses: 0,
      draws: 0,
      winPercentage: 0,
      averageGameLength: 0
    };
  }

  /**
   * Get current statistics
   */
  getStatistics(userId?: string): ChessStatistics {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      
      if (data) {
        return JSON.parse(data) as ChessStatistics;
      }

      return this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get statistics:', error);
      return this.createEmptyStats();
    }
  }

  /**
   * Record a game result
   */
  recordGameResult(result: ChessGameResult, playerColor: PlayerColor, userId?: string): boolean {
    try {
      const stats = this.getStatistics(userId);

      // Update appropriate category
      if (result.mode === 'singleplayer' && result.difficulty) {
        this.updateGameStats(stats.singlePlayer[result.difficulty], result, playerColor);
      } else if (result.mode === 'multiplayer-realtime') {
        this.updateGameStats(stats.multiplayer.realtime, result, playerColor);
      } else if (result.mode === 'multiplayer-turnbased') {
        this.updateGameStats(stats.multiplayer.turnbased, result, playerColor);
      }

      // Update overall statistics
      stats.totalGames++;
      stats.totalPlayTime += result.duration;
      stats.longestGame = Math.max(stats.longestGame, result.moveCount);

      // Update shortest win
      if (result.winner === playerColor && !result.isDraw) {
        stats.shortestWin = Math.min(stats.shortestWin, result.moveCount);
      }

      // Save updated statistics
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(stats));

      return true;
    } catch (error) {
      console.error('Failed to record game result:', error);
      return false;
    }
  }

  /**
   * Update game stats for a category
   */
  private updateGameStats(
    stats: ChessStatistics['singlePlayer']['beginner'],
    result: ChessGameResult,
    playerColor: PlayerColor
  ): void {
    const totalGames = stats.wins + stats.losses + stats.draws;

    if (result.isDraw) {
      stats.draws++;
    } else if (result.winner === playerColor) {
      stats.wins++;
    } else {
      stats.losses++;
    }

    // Recalculate win percentage
    const newTotalGames = stats.wins + stats.losses + stats.draws;
    stats.winPercentage = newTotalGames > 0 ? (stats.wins / newTotalGames) * 100 : 0;

    // Update average game length
    stats.averageGameLength = 
      (stats.averageGameLength * totalGames + result.moveCount) / newTotalGames;
  }

  /**
   * Get statistics for a specific mode
   */
  getModeStatistics(mode: GameMode, difficulty?: AIDifficulty, userId?: string) {
    const stats = this.getStatistics(userId);

    if (mode === 'singleplayer' && difficulty) {
      return stats.singlePlayer[difficulty];
    } else if (mode === 'multiplayer-realtime') {
      return stats.multiplayer.realtime;
    } else if (mode === 'multiplayer-turnbased') {
      return stats.multiplayer.turnbased;
    }

    return this.createEmptyGameStats();
  }

  /**
   * Get win rate for a specific mode
   */
  getWinRate(mode: GameMode, difficulty?: AIDifficulty, userId?: string): number {
    const stats = this.getModeStatistics(mode, difficulty, userId);
    return stats.winPercentage;
  }

  /**
   * Get total games played
   */
  getTotalGames(userId?: string): number {
    const stats = this.getStatistics(userId);
    return stats.totalGames;
  }

  /**
   * Get total play time in seconds
   */
  getTotalPlayTime(userId?: string): number {
    const stats = this.getStatistics(userId);
    return stats.totalPlayTime;
  }

  /**
   * Get longest game (by move count)
   */
  getLongestGame(userId?: string): number {
    const stats = this.getStatistics(userId);
    return stats.longestGame;
  }

  /**
   * Get shortest win (by move count)
   */
  getShortestWin(userId?: string): number {
    const stats = this.getStatistics(userId);
    return stats.shortestWin === Infinity ? 0 : stats.shortestWin;
  }

  /**
   * Get overall win rate across all modes
   */
  getOverallWinRate(userId?: string): number {
    const stats = this.getStatistics(userId);
    
    let totalWins = 0;
    let totalGames = 0;

    // Sum single player stats
    Object.values(stats.singlePlayer).forEach(modeStats => {
      totalWins += modeStats.wins;
      totalGames += modeStats.wins + modeStats.losses + modeStats.draws;
    });

    // Sum multiplayer stats
    Object.values(stats.multiplayer).forEach(modeStats => {
      totalWins += modeStats.wins;
      totalGames += modeStats.wins + modeStats.losses + modeStats.draws;
    });

    return totalGames > 0 ? (totalWins / totalGames) * 100 : 0;
  }

  /**
   * Get statistics summary
   */
  getStatisticsSummary(userId?: string) {
    const stats = this.getStatistics(userId);

    return {
      totalGames: stats.totalGames,
      totalPlayTime: stats.totalPlayTime,
      overallWinRate: this.getOverallWinRate(userId),
      longestGame: stats.longestGame,
      shortestWin: this.getShortestWin(userId),
      singlePlayerGames: 
        stats.singlePlayer.beginner.wins + stats.singlePlayer.beginner.losses + stats.singlePlayer.beginner.draws +
        stats.singlePlayer.intermediate.wins + stats.singlePlayer.intermediate.losses + stats.singlePlayer.intermediate.draws +
        stats.singlePlayer.expert.wins + stats.singlePlayer.expert.losses + stats.singlePlayer.expert.draws,
      multiplayerGames:
        stats.multiplayer.realtime.wins + stats.multiplayer.realtime.losses + stats.multiplayer.realtime.draws +
        stats.multiplayer.turnbased.wins + stats.multiplayer.turnbased.losses + stats.multiplayer.turnbased.draws
    };
  }

  /**
   * Reset statistics
   */
  resetStatistics(userId?: string): boolean {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const emptyStats = this.createEmptyStats();
      // Save empty stats to localStorage directly
      localStorage.setItem(key, JSON.stringify(emptyStats));
      return true;
    } catch (error) {
      console.error('Failed to reset statistics:', error);
      return false;
    }
  }

  /**
   * Export statistics to JSON
   */
  exportStatistics(userId?: string): string {
    const stats = this.getStatistics(userId);
    return JSON.stringify(stats, null, 2);
  }

  /**
   * Import statistics from JSON
   */
  importStatistics(jsonString: string, userId?: string): boolean {
    try {
      const stats: ChessStatistics = JSON.parse(jsonString);
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      // Save imported stats to localStorage directly
      localStorage.setItem(key, JSON.stringify(stats));
      return true;
    } catch (error) {
      console.error('Failed to import statistics:', error);
      return false;
    }
  }
}
